/* eslint-disable no-nested-ternary */
/**
 * ConstructorTransformer.js
 * Transforms Constructor.io Search API response → JCP Solr response shape.
 * Used by GallerySaga for search pages when Constructor is enabled.
 *
 * All functions are null-safe — if Constructor returns unexpected data,
 * the transformer returns valid empty structures so reducers don't crash.
 */

import {
    CONSTRUCTOR_IGNORE_FILTER_PARAMS,
    CONSTRUCTOR_FACET_CONFIG,
    PREVIEWABLE_FIELDS_KEY,
    MONTH_ABBR,
} from '../common/constants';
import { isStoreFacet } from './ConstructorBOPISHelper';

function hashCode(str) {
    if (!str || typeof str !== 'string') return 0;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }
    return Math.abs(hash);
}

function slugify(str) {
    if (!str || typeof str !== 'string') return '';
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

function encodeParam(value) {
    return encodeURIComponent(value).replace(/%20/g, '+').replace(/!/g, '%21');
}

function buildCanonicalURL(term, facets, pageType, categoryId, galleryPath, brandParam) {
    let url;
    if (pageType === 'g') {
        const path = galleryPath || 'g';
        if (categoryId) {
            url = `/${path}?id=${encodeURIComponent(categoryId)}`;
        } else if (brandParam) {
            // Brand page without category ID - preserve brand param
            url = `/${path}?brand=${encodeURIComponent(brandParam)}`;
        } else {
            url = `/${path}`;
        }
    } else {
        url = `/s?searchTerm=${encodeURIComponent(term || '')}`;
    }
    if (!Array.isArray(facets)) return url;
    facets.forEach((facet) => {
        if (!facet || !Array.isArray(facet.options)) return;
        facet.options.forEach((opt) => {
            if (opt && opt.status === 'selected' && opt.value) {
                url += `&${encodeURIComponent(facet.name || '')}=${encodeURIComponent(opt.value)}`;
            }
        });
    });
    return url;
}

/**
 * Returns just the path portion (no query string) for gallery/search API URLs.
 * Used to build URLs where facets must be inserted before id=/searchTerm=.
 */
function getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam) {
    if (pageType === 'g') {
        const path = galleryPath || 'g';
        if (categoryId || brandParam) {
            return `/v1/search-service/${path}`;
        }
    }
    return '/v1/search-service/s';
}

/**
 * Returns the anchor query param(s) that must come after facets in the URL.
 * For gallery: ?id=cat... or ?brand=...
 * For search: ?searchTerm=...
 */
function getBaseApiAnchorParam(term, pageType, categoryId, brandParam) {
    if (pageType === 'g') {
        if (categoryId) return `id=${encodeURIComponent(categoryId)}`;
        if (brandParam) return `brand=${encodeURIComponent(brandParam)}`;
    }
    const encodedTerm = encodeURIComponent(term || '').replace(/%20/g, '+');
    return `searchTerm=${encodedTerm}`;
}

/**
 * Preview: Returns true if the given facet name is a preview-hidden facet whose values
 * contain a |dayKey suffix that should be stripped from display/URL.
 */
function isPreviewHiddenFacet(facetName, previewDayKey, previewDateFacetOptions) {
    return (
        !!previewDayKey &&
        Array.isArray(previewDateFacetOptions) &&
        previewDateFacetOptions.includes(facetName)
    );
}

/**
 * Strips the |dayKey suffix from a preview facet value for display/URL use.
 * e.g. "SALE|19Sep2026" → "SALE"
 */
export function stripPreviewSuffix(value) {
    if (!value || !value.includes('|')) return value;
    return value.split('|')[0];
}

/**
 * Preview time-level markers.
 *
 * Marker / pf-key formats supported
 * Day level (legacy):   label|DDMonYYYY "SALE|29Sep2026"
 * Time level (new):     label|DDMonYYYY|HHmm|HHmm "POWER PENNEY DEAL!|29Sep2026|0000|1259"
 *
 * A day-only value is treated as a whole-day window (0000-2359) for backward
 * HHmm is 24-hour, zero-padded, minute precision (no seconds), and is expressed in the SAME wall-clock (Central) that the preview datetime / cookie use
 */

/**
 * Converts an "HHmm" string to minutes-of-day, or null if not a valid HHmm.
 * @param {string} hhmm - e.g. "0000", "1300", "2359"
 * @returns {number|null} minutes 0-1439, or null
 */
function hhmmToMinutes(hhmm) {
    if (typeof hhmm !== 'string' || !/^\d{4}$/.test(hhmm)) return null;
    const h = parseInt(hhmm.slice(0, 2), 10);
    const m = parseInt(hhmm.slice(2, 4), 10);
    if (h > 23 || m > 59) return null;
    return h * 60 + m;
}

/**
 * Parses a composite preview value or pf key into its window parts.
 * Returns null for empty/invalid input.
 *
 * @param {string} value - e.g. "SALE|29Sep2026|1300|2359" or "SALE|29Sep2026" or a bare pf key "29Sep2026|0000|1259" / "29Sep2026".
 * @returns {{ label: string, date: string, start: number, end: number, hasTime: boolean } | null}
 * start/end are minutes-of-day (0-1439). Day-only values return the full-day window.
 */
function parsePreviewWindow(value) {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('|');

    // A pf key has no label (starts with the date); a marker leads with the label.
    // Detect by testing whether the first segment looks like a DDMonYYYY date.
    const dateRe = /^\d{2}[A-Za-z]{3}\d{4}$/;
    let label = '';
    let dateIdx = 0;
    if (!dateRe.test(parts[0])) {
        // First segment is the label; date is next.
        [label] = parts;
        dateIdx = 1;
    }

    const date = parts[dateIdx];
    if (!date || !dateRe.test(date)) return null;

    const startRaw = parts[dateIdx + 1];
    const endRaw = parts[dateIdx + 2];
    const hasTime = hhmmToMinutes(startRaw) !== null && hhmmToMinutes(endRaw) !== null;

    return {
        label,
        date,
        start: hasTime ? hhmmToMinutes(startRaw) : 0,
        end: hasTime ? hhmmToMinutes(endRaw) : 1439,
        hasTime,
    };
}

/**
 * Selects the single active window for a preview datetime from a list of candidate values (composite markers or pf keys).
 *
 * Rules:
 *   - The candidate's date must equal the selected day (previewDayKey).
 *   - Selection range is inclusive on both ends: start <= T <= end.
 *   - Adjacent windows are split at the minute boundary (earlier ends HHmm-1, next starts HHmm), so the boundary minute belongs to the LATER window. When two candidates could match, the one
 *     with the later start wins to honor that convention.
 *   - Day-only candidates match any time on that date (whole-day window).
 *
 * @param {string[]} candidates - marker values or pf keys for one product/field
 * @param {string} previewDayKey - DDMonYYYY of the selected preview datetime
 * @param {number} previewMinutes - minutes-of-day (0-1439) of the selected preview datetime, Central
 * @returns {string|null} the matching candidate string, or null if none match (caller falls back)
 */
export function selectActivePreviewWindow(candidates, previewDayKey, previewMinutes) {
    if (!Array.isArray(candidates) || !previewDayKey) return null;
    const t = typeof previewMinutes === 'number' ? previewMinutes : 0;

    let best = null;
    let bestStart = -1;
    candidates.forEach((candidate) => {
        const win = parsePreviewWindow(candidate);
        if (!win || win.date !== previewDayKey) return;
        if (t < win.start || t > win.end) return;
        // Prefer the window with the later start (boundary minute -> later window).
        if (win.start > bestStart) {
            best = candidate;
            bestStart = win.start;
        }
    });
    return best;
}

/**
 * Returns ALL candidate values whose window contains the preview time
 *
 * the day-level (no-time) value is NOT included when any time window matches.
 * The day-level value is returned solely as a fallback when NO time window matches (day-only /
 * backward-compat products, where the day-level value is the only representation available).
 *
 * @param {string[]} candidates - marker values for one label+day
 * @param {string} previewDayKey - DDMonYYYY of the preview datetime
 * @param {number} previewMinutes - minutes-of-day (0-1439), Central
 * @returns {string[]} the active window values, or [day-level value] when no window matches, or []
 */
export function selectActivePreviewWindows(candidates, previewDayKey, previewMinutes) {
    if (!Array.isArray(candidates) || !previewDayKey) return [];
    const t = typeof previewMinutes === 'number' ? previewMinutes : 0;

    const sameDay = candidates
        .map((candidate) => ({ candidate, win: parsePreviewWindow(candidate) }))
        .filter(({ win }) => win && win.date === previewDayKey);

    // Time-windowed values whose [start,end] contains the preview minute.
    const matchingWindows = sameDay
        .filter(({ win }) => win.hasTime && t >= win.start && t <= win.end)
        .map(({ candidate }) => candidate);

    if (matchingWindows.length > 0) return matchingWindows;

    // No time window matched: fall back to the day-level (no-time) value if present.
    const dayLevel = sameDay.filter(({ win }) => !win.hasTime).map(({ candidate }) => candidate);
    return dayLevel;
}

/**
 * Resolves the active price/title facts bucket for a preview datetime from the pf day bucket.
 *
 * The day bucket (pf[dayKey]) carries the day-level facts (price_min, marketing_label, …) at
 * its top level AND, when the feed is time-level, one or more "HHmm|HHmm" sub-objects as
 * sibling keys, each holding that window's facts.
 *
 * Resolution order:
 *   1. Time window: among the bucket's "HHmm|HHmm" sub-keys, pick the one whose [start,end]
 *      contains the preview time (inclusive; boundary minute -> later window). Use its facts.
 *   2. No matching time sub-key (or none exist): fall back to the day-level fields on the
 *      bucket itself.
 * (If pf[dayKey] is absent entirely, the caller passes nothing here and pricing falls back to
 * the product's base `data` fields.)
 *
 * @param {object} dayBucket - the value stored under pf[dayKey]
 * @param {number} previewMinutes - minutes-of-day (0-1439) of the preview datetime, Central
 * @returns {object|null} the facts object to render from, or null if the bucket is invalid
 */
function resolvePreviewPfBucket(dayBucket, previewMinutes) {
    if (!dayBucket || typeof dayBucket !== 'object') return null;

    // 1. Look for a time-window sub-key ("HHmm|HHmm") that contains the preview time.
    const t = typeof previewMinutes === 'number' ? previewMinutes : 0;
    let best = null;
    let bestStart = -1;
    Object.keys(dayBucket).forEach((subKey) => {
        const parts = subKey.split('|');
        // Only consider keys shaped exactly like "HHmm|HHmm"; ignore the day-level fact fields.
        if (parts.length !== 2) return;
        const start = hhmmToMinutes(parts[0]);
        const end = hhmmToMinutes(parts[1]);
        if (start === null || end === null) return;
        if (t < start || t > end) return;
        // Boundary minute belongs to the later window -> latest matching start wins.
        if (start > bestStart) {
            best = dayBucket[subKey];
            bestStart = start;
        }
    });
    if (best) return best;

    // 2. No matching time window: fall back to the day-level fields on the bucket itself.
    return dayBucket;
}

/**
 * Collapses a preview date facet's options to one option per label, choosing the value that applies at the preview time
 *
 * For each label (e.g. "POWER PENNEY DEAL!") the facet may carry, for the selected day:
 *   - a day-level value  "LABEL|DDMonYYYY" (no time segments)
 *   - one or more window values "LABEL|DDMonYYYY|HHmm|HHmm"
 * We keep only one:
 *   - the window whose [start,end] contains previewMinutes (inclusive; boundary -> later),
 *   - else the day-level value,
 *
 * @param {object[]} rawOptions - facet.options from Constructor
 * @param {string} facetName
 * @param {string} previewDayKey - DDMonYYYY of the preview datetime (empty when not preview)
 * @param {number} previewMinutes - minutes-of-day of the preview datetime, Central
 * @param {string[]} previewDateFacetOptions - facet names that use preview date suffixes
 * @returns {object[]} deduped options
 */
function dedupePreviewFacetOptions(
    rawOptions,
    facetName,
    previewDayKey,
    previewMinutes,
    previewDateFacetOptions
) {
    if (!isPreviewHiddenFacet(facetName, previewDayKey, previewDateFacetOptions)) {
        return rawOptions;
    }
    if (!Array.isArray(rawOptions) || rawOptions.length === 0) return rawOptions;

    // Group this day's options by label
    const groups = new Map(); // label -> option[]
    const passthrough = [];
    rawOptions.forEach((opt) => {
        const win = opt && typeof opt.value === 'string' ? parsePreviewWindow(opt.value) : null;
        if (!win || win.date !== previewDayKey) {
            passthrough.push(opt);
            return;
        }
        const label = win.label || stripPreviewSuffix(opt.value);
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(opt);
    });

    const result = [...passthrough];
    groups.forEach((opts) => {
        const values = opts.map((o) => o.value);
        const activeValues = selectActivePreviewWindows(values, previewDayKey, previewMinutes);
        const dayLevelOpt = opts.find((o) => {
            const w = parsePreviewWindow(o.value);
            return w && !w.hasTime;
        });
        // Displayed option:  Display collapses to one row per label regardless.
        const primaryActive = selectActivePreviewWindow(values, previewDayKey, previewMinutes);
        let chosen =
            (primaryActive && opts.find((o) => o.value === primaryActive)) ||
            dayLevelOpt ||
            opts[0];
        const anySelected = opts.some((o) => o && o.status === 'selected');
        const activeOriginalValues = activeValues.length > 0 ? activeValues : [chosen.value];
        /* Count = sum of the counts of EXACTLY the options we send. Could be inflated since we are calculating */
        const activeSet = new Set(activeOriginalValues);
        const combinedCount = opts
            .filter((o) => activeSet.has(o.value))
            .reduce((sum, o) => sum + (o.count || 0), 0);
        chosen = {
            ...chosen,
            count: combinedCount,
            // Carry the FULL active set so the saga re-sends every overlapping window
            activeOriginalValues,
            ...(anySelected && chosen.status !== 'selected' ? { status: 'selected' } : {}),
        };
        result.push(chosen);
    });
    return result;
}

/**
 * Builds a query string of passthrough params that should be preserved in facet/sort URLs.
 * These params are not filters but should remain in the URL during navigation.
 *
 * Passthrough params are determined dynamically: any URL param that is NOT in
 * CONSTRUCTOR_IGNORE_FILTER_PARAMS and is NOT a recognized facet name will be
 * automatically passed through. This allows unknown/custom params to be preserved
 * without requiring explicit configuration.
 *
 * @param {object} params - URL params object
 * @param {Array} facetNames - Array of facet names from the current response (optional)
 * @returns {object} - Map of passthrough params { key: encodedValue } for inclusion in facetsMap
 */
function getPassthroughParamsMap(params, facetNames = []) {
    if (!params || typeof params !== 'object') return {};

    // Build a Set of ignored params (lowercase for case-insensitive matching)
    const ignoreSet = new Set(
        CONSTRUCTOR_IGNORE_FILTER_PARAMS.map((p) => (p ? p.toLowerCase() : p))
    );

    // Build a Set of known facet names (lowercase for case-insensitive matching)
    const facetSet = new Set(facetNames.map((f) => (f ? f.toLowerCase() : f)));

    // System params that are handled separately and should not be passed through
    const systemParams = new Set(['productgridview', 'id', 'searchterm', 'brand']);

    const result = {};
    Object.keys(params).forEach((key) => {
        const keyLower = key.toLowerCase();
        // Pass through if: not ignored, not a facet, not a system param, and has a value
        if (
            !ignoreSet.has(keyLower) &&
            !facetSet.has(keyLower) &&
            !systemParams.has(keyLower) &&
            params[key]
        ) {
            result[key] = encodeParam(params[key]);
        }
    });
    return result;
}

/**
 * Builds a full API URL with facets in alphabetical order before id=/searchTerm=.
 * facetsMap: { facetName: 'encodedValue', ... } — only include facets that should appear.
 * trailingParams: string of additional params to append after productGridView (e.g. &sort=BS)
 */
function buildFacetUrl(pathOnly, facetsMap, anchorParam, trailingParams) {
    const anchorKey = anchorParam ? anchorParam.slice(0, anchorParam.indexOf('=')) : '';
    const sortedFacetParams = Object.keys(facetsMap)
        .sort()
        .filter((k) => facetsMap[k])
        .map((k) => `${k}=${facetsMap[k]}`)
        .join('&');
    if (anchorKey === 'brand') {
        // Brand pages: all params alphabetical including brand=
        const allParams = { ...facetsMap, [anchorKey]: anchorParam.slice(anchorKey.length + 1) };
        const sorted = Object.keys(allParams)
            .sort()
            .filter((k) => allParams[k])
            .map((k) => `${k}=${allParams[k]}`)
            .join('&');
        return `${pathOnly}?${sorted}&productGridView=medium${trailingParams || ''}`;
    }
    if (anchorKey === 'searchTerm') {
        // Search pages: searchTerm= first, then facets alphabetically
        const facetPart = sortedFacetParams ? `&${sortedFacetParams}` : '';
        return `${pathOnly}?${anchorParam}${facetPart}&productGridView=medium${
            trailingParams || ''
        }`;
    }
    // Gallery pages: facets alphabetically, then id= after
    const facetPart = sortedFacetParams ? `${sortedFacetParams}&` : '';
    return `${pathOnly}?${facetPart}${anchorParam}&productGridView=medium${trailingParams || ''}`;
}

/**
 * Builds the base API path for facet/sort/chip URLs based on page type.
 * Search: /v1/search-service/s?searchTerm={term}
 * Gallery/Browse: /v1/search-service/{pathname}?id={categoryId}
 * Brand pages: /v1/search-service/{pathname}?brand={brandValue}
 * The gallery pathname preserves the original URL path (e.g., /g/men/mens-socks or /g/brand)
 */
function getBaseApiPath(term, pageType, categoryId, galleryPath, brandParam) {
    if (pageType === 'g') {
        const path = galleryPath || 'g';
        if (categoryId) {
            return `/v1/search-service/${path}?id=${encodeURIComponent(categoryId)}`;
        }
        if (brandParam) {
            // Brand page without category ID - preserve brand param
            return `/v1/search-service/${path}?brand=${encodeURIComponent(brandParam)}`;
        }
        // Fallback to search if no categoryId or brandParam
    }
    const encodedTerm = encodeURIComponent(term || '').replace(/%20/g, '+');
    return `/v1/search-service/s?searchTerm=${encodedTerm}`;
}


function roundPrice(value) {
    if (typeof value !== 'number' || isNaN(value)) return 0;
    return Math.round(value * 100) / 100;
}

function transformProduct(result, searchTerm, config) {
    if (!result || typeof result !== 'object') {
        return { name: '', ppId: '', skuSwatch: [], availabilityStatus: [], imagesInfo: {} };
    }
    const d = result.data || {};
    // preview: pf holds price/title facts for the selected day. The day bucket
    // (pf[previewDayKey]) carries day-level fields plus optional "HHmm|HHmm" sub-objects.
    // resolvePreviewPfBucket selects the time window containing the preview time (Central
    // minutes) and falls back to the day-level fields when no window matches. If the day bucket
    // is absent entirely, pf is undefined and pricing falls back to the base data fields.
    const previewDayKey = config && config.previewDayKey;
    const previewMinutes = config && config.previewMinutes;
    const previewDayBucket =
        previewDayKey && d[PREVIEWABLE_FIELDS_KEY] && d[PREVIEWABLE_FIELDS_KEY][previewDayKey];
    const pf = previewDayBucket
        ? resolvePreviewPfBucket(previewDayBucket, previewMinutes)
        : undefined;
    const rawOffers = Array.isArray(d.offers) ? d.offers : [];
    const offers = rawOffers.filter((o) => o && o.inventoryStatus === 'Available');
    // Also check for winningOffer (Mirakl marketplace products have this as single object in data)
    const winningOffer = (pf && pf.winningOffer) || d.winningOffer || null;
    const hasMarketplaceOffers =
        offers.some((o) => o && o.shopName && o.shopName !== '') ||
        (winningOffer && winningOffer.shop_name);
    const sourceType = hasMarketplaceOffers ? 'MKL' : d.source === 'MKL' ? 'MKL' : null;

    let currentMin = 0;
    let currentMax = 0;
    if (winningOffer && winningOffer.Offer_Min_Price) {
        // Mirakl winning offer takes priority
        currentMin = roundPrice(winningOffer.Offer_Min_Price);
        currentMax = roundPrice(winningOffer.Offer_Max_Price || winningOffer.Offer_Min_Price);
    } else if (offers.length > 0) {
        const minPrices = offers.map((o) =>
            typeof o.offerMinPrice === 'number' ? o.offerMinPrice : 0
        );
        const maxPrices = offers.map((o) =>
            typeof o.offerMaxPrice === 'number' ? o.offerMaxPrice : 0
        );
        currentMin = roundPrice(Math.min(...minPrices));
        currentMax = roundPrice(Math.max(...maxPrices));
    } else {
        currentMin = roundPrice((pf && pf.price_min) ?? d.price_min ?? d.price ?? 0);
        currentMax = roundPrice((pf && pf.price_max) ?? d.price_max ?? d.price ?? currentMin);
    }
    const originalMin = roundPrice(
        (pf && pf.original_price_min) ?? d.original_price_min ?? currentMin
    );
    const originalMax = roundPrice(
        (pf && pf.original_price_max) ?? d.original_price_max ?? currentMax
    );

    // Preview: honoring the price type attribute in pf over clearance flag
    let priceType = 'ORIGINAL';
    if ((pf && pf.clearance === 'Y') || (pf && pf.price_type === 'CLEARANCE')) {
        priceType = 'CLEARANCE';
    } else if (!pf && d.clearance === 'Y') {
        priceType = 'CLEARANCE';
    } else if (currentMin < originalMin) {
        priceType = 'SALE';
    }

    let marketingLabel = (pf && pf.marketing_label) || d.marketing_label || '';
    if (winningOffer && winningOffer.marketing_label) {
        marketingLabel = winningOffer.marketing_label;
    }

    let bulletedCopyAttrs = [];
    if (d.description && typeof d.description === 'string') {
        try {
            const m = d.description.match(/<li>(.*?)<\/li>/g);
            if (m) bulletedCopyAttrs = m.map((li) => li.replace(/<\/?li>/g, ''));
        } catch (e) {
            bulletedCopyAttrs = [];
        }
    }

    const badges = Array.isArray(d.badges) ? d.badges : [];
    const upperLeftBadge = [];
    const bottomBadges = [];
    if (d.bundle_indicator) {
        upperLeftBadge.push({
            name: `Save up to $${roundPrice(Math.max(0, originalMin - currentMin)).toFixed(0)}`,
            priority: 1,
            urlParam: 'bundle',
        });
    }
    if (badges.includes('NEW_BADGE') || badges.includes('NEW') || d.arrival === 'NEW') {
        upperLeftBadge.push({ name: 'NEW', priority: 2, urlParam: 'new' });
    }
    if (badges.includes('FEW_LEFT')) {
        upperLeftBadge.push({ name: 'Few Left', priority: 3, urlParam: 'fewleft' });
    }
    if (badges.includes('ONLY_AT_JCP')) {
        upperLeftBadge.push({ name: 'Only at JCP', priority: 4, urlParam: 'onlyatjcp' });
    }
    badges.forEach((b, i) => {
        if (
            typeof b === 'string' &&
            (b.includes('HUSKY') || b.includes('PLUS') || b.includes('PETITE'))
        ) {
            bottomBadges.push({
                name: b.replace(/_/g, ' ').replace('APPAREL ', ''),
                priority: i + 1,
                urlParam: slugify(b),
            });
        }
    });

    const variationsMap = Array.isArray(result.variations_map) ? result.variations_map : [];
    const skuSwatch = variationsMap
        .map((swatch, i) => {
            if (!swatch || typeof swatch !== 'object') return null;
            if (sourceType !== 'MKL' && !swatch.swatch_image) {
                return null;
            }
            const altImgs = Array.isArray(swatch.alt_images) ? swatch.alt_images : [];
            return {
                colorName: swatch.colors || swatch.color || '',
                swatchImageId: swatch.swatch_image || '',
                colorizedImageId: swatch.image_url || '',
                colorSeqNum: i + 1,
                skuId: swatch.variation_id || d.variation_id || '',
                defaultSwatch: swatch.is_default || false,
                skuAltImages: altImgs.map((img, j) => ({
                    imgId: typeof img === 'string' ? img : '',
                    colorSeq: j,
                })),
            };
        })
        .filter(Boolean);

    let pdpUrl = '';
    if (d.url && typeof d.url === 'string') {
        pdpUrl = d.url;
        // Only append searchTerm for search pages (pageType='s'), not gallery/browse pages (pageType='g')
        // Gallery pages use categoryId, not searchTerm, so appending it creates invalid URLs
        const isSearchPage = config && config.pageType === 's';
        if (isSearchPage && searchTerm) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl = `${pdpUrl}${urlParamSeparator}searchTerm=${encodeURIComponent(
                searchTerm
            ).replace(/%20/g, '+')}`;
        }
        // Append facetSelected param when PDP-enabled facets are active (matches Solr behavior)
        if (config && config.selectedFacets && config.selectedFacets.length > 0) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl += `${urlParamSeparator}facetSelected=${config.selectedFacets.join(',')}`;
        }
        // Append selectedSKUId when color facet is active — use the product's default variation_id
        // Solr always appends the skuId (default variation) when facetSelected is active,
        // PDP uses facetSelected + selectedSKUId together to determine which swatch to display
        if (config && config.selectedFacets && config.selectedFacets.length > 0) {
            const skuId = d.variation_id || '';
            if (skuId) {
                const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
                pdpUrl += `${urlParamSeparator}selectedSKUId=${skuId}`;
            }
        }
        // Append storeIds to PDP URL when same-day-pickup is active (matches Solr behavior)
        if (config && config.params && config.params.storeIds) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl += `${urlParamSeparator}storeIds=${encodeURIComponent(config.params.storeIds)}`;
        }
        // Append geoZip to PDP URL when localization is active (matches Solr behavior)
        if (config && config.params && config.params.geoZip) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl += `${urlParamSeparator}geoZip=${encodeURIComponent(config.params.geoZip)}`;
        }
    }

    return {
        name: d.product_title || result.value || '',
        ppId: d.id || '',
        averageRating: d.rating || 0,
        reviewCount: d.review_count || 0,
        currentMin,
        currentMax,
        originalMin,
        originalMax,
        priceType,
        currentPriceLabel: priceType === 'ORIGINAL' ? '' : priceType,
        originalPriceLabel: priceType === 'SALE' || priceType === 'CLEARANCE' ? 'original' : '',
        minSavePrice:
            d.coupon_price_min || d.coupon_price_max
                ? 0
                : originalMin > 0 && currentMin < originalMin
                ? Math.round(((originalMin - currentMin) / originalMin) * 100)
                : 0,
        maxSavePrice:
            d.coupon_price_min || d.coupon_price_max
                ? 0
                : originalMax > 0 && currentMax < originalMax
                ? Math.round(((originalMax - currentMax) / originalMax) * 100)
                : 0,
        brand: d.brand || '',
        skuId: d.variation_id || '',
        pdpUrl,
        boostedPP: result.is_slotted || false,
        preOrder: d.preorder || false,
        videoId: d.video_id || '',
        customerFavourite: d.customer_favorite || false,
        marketingLabel,
        upperLeftBadge,
        bottomBadges,
        mapPrice:
            typeof d.marketing_promotions === 'string' &&
            d.marketing_promotions.includes('VIEW PRICE IN BAG'),
        imagesInfo: {
            /*
             * ENSEMBLE products always use group_image
             * if color filter is selected use image_url for  non-ensembles
             * Products whose data field matches a configured value in groupImageTypes use group_image
             */
            thumbnailImageId: (() => {
                if (!d.group_image) return d.image_url || '';
                if (d.entity_type === 'ENSEMBLE') return d.group_image;
                if (config.isColorFilterSelected) return d.image_url || '';

                const groupImageTypes = config.groupImageTypes || {};
                const matchesType = Object.keys(groupImageTypes).some((field) => {
                    const values = groupImageTypes[field];
                    if (!Array.isArray(values) || !d[field]) return false;
                    const fieldValues = (Array.isArray(d[field]) ? d[field] : [d[field]]).map((v) =>
                        String(v).toLowerCase()
                    );
                    return values.some((value) =>
                        fieldValues.includes(String(value).toLowerCase())
                    );
                });

                return matchesType ? d.group_image : d.image_url || '';
            })(),
            altThumbnailImageId: (Array.isArray(d.alt_images) && d.alt_images[0]) || '',
        },
        currencyCode: 'USD',
        skuSwatch,
        sephoraIn: false,
        showComparison: priceType !== 'ORIGINAL' || !!d.bundle_indicator,
        truckItemIn: d.truckable || false,
        ecoRebate: d.rebate_eligible || false,
        bundleStatusBadge: d.bundle_indicator || false,
        bundleIndicator: !!(d.bundle_indicator && d.template_type === 'package'),
        appendSKUId: false,
        enableMiniPDP: (() => {
            // Determine template type: use data field first, fallback to pTmplType in URL
            const templateType =
                d.template_type ||
                (d.url && d.url.match(/pTmplType=([^&]+)/)
                    ? d.url.match(/pTmplType=([^&]+)/)[1]
                    : '');
            const allowedTypes = (config && config.templateTypesForMiniPdp) || [
                'regular',
                'sephora',
                'beauty',
            ];
            return !templateType || allowedTypes.includes(templateType);
        })(),
        subDivision: d.subdivision_id || '',
        firstActivationDate: d.first_active_date || '',
        bulletedCopyAttrs,
        fpacPriceMin: (pf && pf.coupon_price_min) ?? d.coupon_price_min ?? null,
        fpacPriceMax: (pf && pf.coupon_price_max) ?? d.coupon_price_max ?? null,
        fpacCoupon: (pf && pf.coupon_code) || d.coupon_code || null,
        channelMessage: d.online_only === 'Y' ? 'Online Only' : '',
        promotionMessages: (() => {
            const promo = Array.isArray(d.marketing_promotions)
                ? d.marketing_promotions[0]
                : d.marketing_promotions;
            return promo ? [{ message: promo, nfor: false }] : [];
        })(),
        availabilityStatus: [],
        newArrival: d.arrival === 'NEW' || badges.includes('NEW_BADGE') || badges.includes('NEW'),
        sourceType,
        lotId: d.lot_id || '',
        bopisEligibility: d.bopis_eligibility === 'Y',
        offers: winningOffer
            ? [
                  {
                      offerId: winningOffer.Offer_Id || '',
                      shopId: winningOffer.shop_id || '',
                      shopName: winningOffer.shop_name || '',
                      skuId: winningOffer.variation_id || '',
                      offerMinPrice: winningOffer.Offer_Min_Price || 0,
                      offerMaxPrice: winningOffer.Offer_Max_Price || 0,
                      inventoryStatus:
                          winningOffer.Inventory_status === 1 ? 'Available' : 'Unavailable',
                  },
              ]
            : offers.length > 0
            ? offers
            : undefined,
    };
}

function transformFacet(
    facet,
    term,
    currentSort,
    allFacets,
    params,
    pageType,
    categoryId,
    galleryPath,
    brandParam,
    previewDateFacetOptions,
    previewDayKey,
    appliedFilters,
    useBrowserPath,
    previewMinutes
) {
    if (!facet || typeof facet !== 'object') return null;
    const rawOptions = Array.isArray(facet.options) ? facet.options : [];
    const facetNameForPreview = facet.name || '';
    /* Preview time-level dedup — ONLY in preview (previewDayKey is set). A preview date facet can
     * expose, for the selected day, a day-level value ("POWER PENNEY DEAL!|01Oct2026") plus one or
     * more time-window values ("...|0000|0159", "...|0200|2359"); we collapse to ONE option per
     * label at the preview time. */
    const options = previewDayKey
        ? dedupePreviewFacetOptions(
              rawOptions,
              facetNameForPreview,
              previewDayKey,
              previewMinutes,
              previewDateFacetOptions
          )
        : rawOptions;
    const typeMap = { multiple: 'checkbox', single: 'checkbox', range: 'prange' };
    const facetName = facet.name || '';
    const urlKey = facetName;
    const pathOnly = useBrowserPath
        ? galleryPath
            ? `/${galleryPath}`
            : pageType === 'g'
            ? '/g'
            : '/s'
        : getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam);
    const anchorParam = getBaseApiAnchorParam(term, pageType, categoryId, brandParam);
    const sortParam = currentSort ? `&sort=${currentSort}` : '';
    // Get passthrough params to include in facetsMap for alphabetical sorting
    // Extract facet names from allFacets for the passthrough logic
    const facetNames = Array.isArray(allFacets)
        ? allFacets.map((f) => f && f.name).filter(Boolean)
        : [];
    const passthroughParamsMap = getPassthroughParamsMap(params, facetNames);
    const trailingParams =
        sortParam +
        (params && params.Nf ? `&Nf=${encodeParam(params.Nf)}` : '') +
        (params && params.Dr ? `&Dr=${encodeParam(params.Dr)}` : '') +
        (params && params.cm_re ? `&cm_re=${encodeParam(params.cm_re)}` : '');

    // Build a map of other currently-selected facets (excluding this facet and store facets)
    const otherFacetsMap = {};
    if (Array.isArray(allFacets)) {
        allFacets.forEach((otherFacet) => {
            if (!otherFacet || otherFacet.name === facetName || !Array.isArray(otherFacet.options))
                return;
            if (otherFacet.name === 'store_availability' || otherFacet.name === 'store_ids') return;
            const otherSelected = otherFacet.options
                .filter((o) => o && o.status === 'selected' && o.value)
                .map((o) => o.value);
            if (otherSelected.length > 0) {
                const displayValues = isPreviewHiddenFacet(
                    otherFacet.name,
                    previewDayKey,
                    previewDateFacetOptions
                )
                    ? otherSelected.map(stripPreviewSuffix)
                    : otherSelected;
                otherFacetsMap[otherFacet.name] = encodeParam(displayValues.sort().join('|'));
            }
        });
    }
    // In preview, persist item_status in facet URLs
    if (
        previewDayKey &&
        appliedFilters &&
        appliedFilters['filters[item_status]'] &&
        facetName !== 'item_status'
    ) {
        const values = appliedFilters['filters[item_status]'];
        if (Array.isArray(values) && values.length > 0) {
            otherFacetsMap.item_status = encodeParam(values.sort().join('|'));
        }
    }
    // Add passthrough params (like view_all) to otherFacetsMap so they sort alphabetically
    Object.assign(otherFacetsMap, passthroughParamsMap);
    /* When store_availability is active, it must sort alphabetically with other facets
     * (before id=) so it goes into facetsMap, not trailing params.
     * storeIds is not a filter — it stays as a trailing param. */
    const storeActive = !!(params && params.storeIds);
    const storeTrailingSuffix = storeActive ? `&storeIds=${params.storeIds}` : '';

    const selectedValues = options
        .filter((o) => o && o.status === 'selected')
        .map((o) => o.value || '')
        .filter(Boolean);

    return {
        facetName: facet.display_name || facet.name || '',
        facetId: hashCode(facetName),
        facetType:
            CONSTRUCTOR_FACET_CONFIG.facetTypes[facetName] ||
            typeMap[facet.type] ||
            CONSTRUCTOR_FACET_CONFIG.defaultFacetType,
        facetInternalName: facetName,
        facetValueCount: options.length,
        noOfSelectedFacetValues: selectedValues.length,
        searchAvailable:
            CONSTRUCTOR_FACET_CONFIG.searchableFiltersMap[facetName] !== undefined &&
            options.length > CONSTRUCTOR_FACET_CONFIG.searchableFiltersMap[facetName],
        minimize: true,
        activeFacet: options.some((o) => o && o.status === 'selected'),
        promotedFilter: false,
        promotedRank: 0,
        facetValueList: options
            .map((opt) => {
                if (!opt || typeof opt !== 'object') return null;
                const optValue = opt.value || '';
                // preview: strip |dayKey suffix from display value for previewDateFacetOptions facets
                // e.g. "SALE|19Sep2026" → display/URL uses "SALE", Constructor receives full value
                const isHiddenFacet = isPreviewHiddenFacet(
                    facetName,
                    previewDayKey,
                    previewDateFacetOptions
                );
                const displayValue = isHiddenFacet ? stripPreviewSuffix(optValue) : optValue;
                const isSelected = opt.status === 'selected';
                const selectedDisplayValues = selectedValues.map((v) =>
                    isHiddenFacet ? stripPreviewSuffix(v) : v
                );
                let facetValueForUrl;
                if (isSelected) {
                    facetValueForUrl = selectedDisplayValues
                        .filter((v) => v !== displayValue)
                        .sort()
                        .join('|');
                } else {
                    facetValueForUrl = [...selectedDisplayValues, displayValue].sort().join('|');
                }
                const encodedFacetValue = encodeParam(facetValueForUrl);
                const facetsMap = { ...otherFacetsMap };

                let apiUrl;
                if (facetName === 'store_availability') {
                    // store_availability is a toggle — build URL with filters[store_availability] and filters[store_ids]
                    const storeOnlyFacetsMap = { ...otherFacetsMap };
                    delete storeOnlyFacetsMap.store_availability;
                    if (isSelected) {
                        // Toggle OFF: remove store filters, keep everything else
                        apiUrl = buildFacetUrl(
                            pathOnly,
                            storeOnlyFacetsMap,
                            anchorParam,
                            trailingParams
                        );
                    } else {
                        // Toggle ON: put store_availability in facetsMap so it sorts alphabetically before id=
                        const storeOnFacetsMap = {
                            ...storeOnlyFacetsMap,
                            store_availability: 'same+day+pickup',
                        };
                        const storeOnTrailing =
                            trailingParams +
                            (params && params.storeIds ? `&storeIds=${params.storeIds}` : '');
                        apiUrl = buildFacetUrl(
                            pathOnly,
                            storeOnFacetsMap,
                            anchorParam,
                            storeOnTrailing
                        );
                    }
                } else {
                    if (encodedFacetValue) facetsMap[urlKey] = encodedFacetValue;
                    if (storeActive) facetsMap.store_availability = 'same+day+pickup';
                    apiUrl = buildFacetUrl(
                        pathOnly,
                        facetsMap,
                        anchorParam,
                        trailingParams + storeTrailingSuffix
                    );
                }
                const displayName =
                    isHiddenFacet && opt.display_name
                        ? stripPreviewSuffix(opt.display_name)
                        : opt.display_name || displayValue;
                return {
                    name: displayName || '',
                    selected: isSelected,
                    count: opt.count || 0,
                    apiUrl,
                    icon: (opt.data && opt.data.icon) || '',
                    imageUrl: (opt.data && opt.data.icon) || '',
                    value: (() => {
                        // For avgrating facets, extract numeric value from option name (e.g. "5 rating" → 5)
                        const optName = displayName || displayValue || '';
                        const match = optName.match(/^(\d+)/);
                        return match ? parseInt(match[1], 10) : 0;
                    })(),
                    facetNValue: '',
                    facetValue: displayValue,
                    /* originalValue: the unstripped facet value from Constructor.
                     * For preview time-level facets this retains the full
                     * "label|DDMonYYYY|HHmm|HHmm" window string so the saga can send the
                     * time-correct value back to Constructor. It is intentionally NOT used
                     * to build the URL, chip name, or display name (those stay stripped).
                     * For non-preview facets it equals the display value (no suffix). */
                    originalValue: optValue,
                    /* activeOriginalValues: deduped preview options; undefined elsewhere. */
                    ...(Array.isArray(opt.activeOriginalValues)
                        ? { activeOriginalValues: opt.activeOriginalValues }
                        : {}),
                    solrFacetName: facetName,
                };
            })
            .filter(Boolean)
            .sort((a, b) => (b.selected ? 1 : 0) - (a.selected ? 1 : 0)),
    };
}

function transformSortOptions(
    constructorSortOptions,
    term,
    facets,
    params,
    pageType,
    categoryId,
    galleryPath,
    brandParam,
    previewDayKey,
    previewDateFacetOptions
) {
    const pathOnly = getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam);
    const anchorParam = getBaseApiAnchorParam(term, pageType, categoryId, brandParam);

    const facetsMap = {};
    if (Array.isArray(facets)) {
        facets.forEach((facet) => {
            if (!facet || !Array.isArray(facet.options) || !facet.name) return;
            if (facet.name === 'store_availability' || facet.name === 'store_ids') return;
            const selectedVals = facet.options
                .filter((opt) => opt && opt.status === 'selected' && opt.value)
                .map((opt) => opt.value);
            if (selectedVals.length > 0) {
                const isPreviewFacet = isPreviewHiddenFacet(
                    facet.name,
                    previewDayKey,
                    previewDateFacetOptions
                );
                const displayVals = isPreviewFacet
                    ? selectedVals.map(stripPreviewSuffix)
                    : selectedVals;
                facetsMap[facet.name] = encodeParam(displayVals.sort().join('|'));
            }
        });
    }
    // Include store_availability in facets map so it sorts alphabetically with other facets
    if (params && params.storeIds) {
        facetsMap.store_availability = 'same+day+pickup';
    }

    let trailingSortParams =
        (params && params.Nf ? `&Nf=${encodeParam(params.Nf)}` : '') +
        (params && params.Dr ? `&Dr=${encodeParam(params.Dr)}` : '');
    if (params && params.storeIds) trailingSortParams += `&storeIds=${params.storeIds}`;
    else if (params && params.store_ids) trailingSortParams += `&storeIds=${params.store_ids}`;
    if (params && params.cm_re) trailingSortParams += `&cm_re=${encodeParam(params.cm_re)}`;
    // Add passthrough params to facetsMap so they sort alphabetically
    const facetNames = Array.isArray(facets) ? facets.map((f) => f && f.name).filter(Boolean) : [];
    Object.assign(facetsMap, getPassthroughParamsMap(params, facetNames));

    const basePath = buildFacetUrl(pathOnly, facetsMap, anchorParam, trailingSortParams);

    // Gallery (browse/PLP) pages show "featured" as the default sort label;
    // search pages show "best match" — mirrors the Java backend FS/BM distinction.
    const isGalleryPage = pageType === 'g';
    const defaultSortName = isGalleryPage ? 'featured' : 'best match';

    if (Array.isArray(constructorSortOptions) && constructorSortOptions.length > 0) {
        const hasRealSortOptions = constructorSortOptions.some(
            (opt) => opt && opt.sort_by && opt.sort_by !== 'relevance'
        );
        if (hasRealSortOptions) {
            return constructorSortOptions
                .map((opt, i) => {
                    if (!opt || typeof opt !== 'object') return null;
                    const sortBy = opt.sort_by || '';
                    const key = sortBy === 'relevance' ? '' : sortBy;
                    const apiUrl = key ? `${basePath}&sort=${key}` : basePath;
                    // Normalize the relevance option display name based on page type
                    const name = sortBy === 'relevance' ? defaultSortName : opt.display_name || '';
                    return {
                        apiUrl,
                        name,
                        key,
                        order: i + 1,
                        selected: opt.status === 'selected',
                    };
                })
                .filter(Boolean);
        }
    }
    return [
        { apiUrl: basePath, name: defaultSortName, key: '', order: 1, selected: true },
        {
            apiUrl: `${basePath}&sort=NA`,
            name: 'new arrivals',
            key: 'NA',
            order: 2,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=BS`,
            name: 'best sellers',
            key: 'BS',
            order: 3,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=RHL`,
            name: 'ratings high - low',
            key: 'RHL',
            order: 4,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=LPLH`,
            name: 'price low - high',
            key: 'LPLH',
            order: 5,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=LPHL`,
            name: 'price high - low',
            key: 'LPHL',
            order: 6,
            selected: false,
        },
    ];
}

export const transformConstructorSearchResponse = (cioResponse, options = {}) => {
    if (!cioResponse || typeof cioResponse !== 'object') {
        return getEmptyResponse(
            options && options.pageType,
            options && options.categoryId,
            options && options.galleryPath
        );
    }

    try {
        const response = cioResponse.response || {};
        const request = cioResponse.request || {};
        const resultId = cioResponse.result_id || '';
        const results = Array.isArray(response.results) ? response.results : [];
        const facets = Array.isArray(response.facets) ? response.facets : [];
        const sortOptions = Array.isArray(response.sort_options) ? response.sort_options : [];

        const page = (options && options.page) || request.page || 1;
        const perPage = request.num_results_per_page || 48;
        const total = response.total_num_results || 0;
        const maxPages = (options && options.maxPaginationPages) || 25;
        const disableDeepPagination = options && options.disableDeepPagination !== false; // default true
        const totalPages = disableDeepPagination
            ? Math.min(Math.ceil(total / (perPage || 48)), maxPages)
            : Math.ceil(total / (perPage || 48));
        const term = request.term || '';
        const pageType = (options && options.pageType) || 's';
        const categoryId = (options && options.categoryId) || '';
        const galleryPath = (options && options.galleryPath) || '';
        // Extract brand param for brand pages (from params.brand or params.Brand)
        // Also check if galleryPath indicates a brand page
        const isBrandPage =
            (galleryPath && galleryPath.includes('g/brand')) ||
            (typeof window !== 'undefined' &&
                window.location &&
                window.location.pathname &&
                window.location.pathname.includes('/g/brand'));
        let brandParam =
            (options && options.params && (options.params.brand || options.params.Brand)) || '';
        // If no brand in params but this is a brand page, try to get from window.location
        if (!brandParam && isBrandPage && typeof window !== 'undefined' && window.location) {
            try {
                brandParam = new URLSearchParams(window.location.search).get('brand') || '';
            } catch (e) {
                brandParam = '';
            }
        }

        const baseApiPath = getBaseApiPath(term, pageType, categoryId, galleryPath, brandParam);

        const requestSortBy = request.sort_by || '';
        const currentSort = requestSortBy && requestSortBy !== 'relevance' ? requestSortBy : '';

        const selectedFacets = [];
        const pathOnly = getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam);
        const anchorParam = getBaseApiAnchorParam(term, pageType, categoryId, brandParam);
        const chipPreviewDateFacetOptions = (options && options.previewDateFacetOptions) || [];
        const chipPreviewDayKey = options && options.previewDayKey;
        facets.forEach((facet) => {
            if (!facet || !Array.isArray(facet.options)) return;
            // Skip store_availability and store_ids from showing as selected filter chips
            if (facet.name === 'store_availability' || facet.name === 'store_ids') return;
            const allSelectedOpts = facet.options.filter((o) => o && o.status === 'selected');
            /* Preview date facets can have MULTIPLE selected option values for the same label. collapse to ONE label */
            const isPreviewChipFacet = isPreviewHiddenFacet(
                facet.name,
                chipPreviewDayKey,
                chipPreviewDateFacetOptions
            );
            const selectedOpts = isPreviewChipFacet
                ? Object.values(
                      allSelectedOpts.reduce((acc, o) => {
                          const label = stripPreviewSuffix(o.value || '');
                          if (label && !acc[label]) acc[label] = o;
                          return acc;
                      }, {})
                  )
                : allSelectedOpts;
            if (selectedOpts.length > 0) {
                selectedFacets.push({
                    facetName: facet.display_name || facet.name || '',
                    facetId: hashCode(facet.name || ''),
                    facetType: 'checkbox',
                    displayFacetName: true,
                    facetInternalName: facet.name || '',
                    facetValueList: selectedOpts.map((opt) => {
                        const remainingValues = selectedOpts
                            .filter((o) => o.value !== opt.value)
                            .map((o) =>
                                isPreviewHiddenFacet(
                                    facet.name,
                                    chipPreviewDayKey,
                                    chipPreviewDateFacetOptions
                                )
                                    ? stripPreviewSuffix(o.value)
                                    : o.value
                            )
                            .filter(Boolean);
                        // Build facets map for chip URL: all selected facets alphabetically before id=
                        const chipFacetsMap = {};
                        facets.forEach((otherFacet) => {
                            if (
                                !otherFacet ||
                                otherFacet.name === facet.name ||
                                !Array.isArray(otherFacet.options)
                            )
                                return;
                            // Skip store-related facets — handled via storeIds/store_availability params
                            if (
                                otherFacet.name === 'store_availability' ||
                                otherFacet.name === 'store_ids'
                            )
                                return;
                            const otherSelected = otherFacet.options
                                .filter((o) => o && o.status === 'selected' && o.value)
                                .map((o) =>
                                    isPreviewHiddenFacet(
                                        otherFacet.name,
                                        chipPreviewDayKey,
                                        chipPreviewDateFacetOptions
                                    )
                                        ? stripPreviewSuffix(o.value)
                                        : o.value
                                );
                            if (otherSelected.length > 0) {
                                chipFacetsMap[otherFacet.name] = encodeParam(
                                    otherSelected.sort().join('|')
                                );
                            }
                        });
                        if (remainingValues.length > 0) {
                            chipFacetsMap[facet.name] = encodeParam(
                                remainingValues.sort().join('|')
                            );
                        }
                        let chipTrailing =
                            (currentSort ? `&sort=${currentSort}` : '') +
                            (options && options.params && options.params.Nf
                                ? `&Nf=${encodeParam(options.params.Nf)}`
                                : '') +
                            (options && options.params && options.params.Dr
                                ? `&Dr=${encodeParam(options.params.Dr)}`
                                : '');
                        if (options && options.params && options.params.storeIds) {
                            chipFacetsMap.store_availability = 'same+day+pickup';
                            chipTrailing += `&storeIds=${options.params.storeIds}`;
                        } else if (options && options.params && options.params.store_ids) {
                            chipFacetsMap.store_availability = 'same+day+pickup';
                            chipTrailing += `&store_ids=${options.params.store_ids}`;
                        }
                        if (options && options.params && options.params.cm_re) {
                            chipTrailing += `&cm_re=${encodeParam(options.params.cm_re)}`;
                        }
                        // Add passthrough params to chipFacetsMap for alphabetical sorting
                        const chipFacetNames = facets.map((f) => f && f.name).filter(Boolean);
                        Object.assign(
                            chipFacetsMap,
                            getPassthroughParamsMap(options && options.params, chipFacetNames)
                        );
                        const chipApiUrl = buildFacetUrl(
                            pathOnly,
                            chipFacetsMap,
                            anchorParam,
                            chipTrailing
                        );
                        return {
                            name: chipPreviewDayKey
                                ? stripPreviewSuffix(
                                      (opt && opt.display_name) || (opt && opt.value) || ''
                                  )
                                : (opt && opt.display_name) || (opt && opt.value) || '',
                            selected: true,
                            count: isPreviewChipFacet
                                ? allSelectedOpts
                                      .filter(
                                          (o) =>
                                              stripPreviewSuffix(o.value || '') ===
                                              stripPreviewSuffix((opt && opt.value) || '')
                                      )
                                      .reduce((sum, o) => sum + (o.count || 0), 0)
                                : (opt && opt.count) || 0,
                            apiUrl: chipApiUrl,
                            facetNValue: '',
                            facetValue: chipPreviewDayKey
                                ? stripPreviewSuffix((opt && opt.value) || '')
                                : (opt && opt.value) || '',
                            solrFacetName: facet.name || '',
                        };
                    }),
                });
            }
        });

        // Inject custom price range (Nf) as selected facet chip
        if (
            options &&
            options.params &&
            options.params.Nf &&
            typeof options.params.Nf === 'string'
        ) {
            const nfVal = options.params.Nf.replace(/\+/g, ' ').trim();
            const nfM = nfVal.match(/^PR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
            if (nfM) {
                const nfFacetsMap = {};
                facets.forEach((f) => {
                    if (
                        !f ||
                        !Array.isArray(f.options) ||
                        f.name === 'store_availability' ||
                        f.name === 'store_ids'
                    )
                        return;
                    const selected = f.options
                        .filter((o) => o && o.status === 'selected' && o.value)
                        .map((o) => o.value);
                    if (selected.length > 0)
                        nfFacetsMap[f.name] = encodeParam(selected.sort().join('|'));
                });
                let nfTrailing =
                    (currentSort ? `&sort=${currentSort}` : '') +
                    (options.params.Dr ? `&Dr=${encodeParam(options.params.Dr)}` : '');
                if (options.params.storeIds)
                    nfTrailing += `&storeIds=${options.params.storeIds}&store_availability=same+day+pickup`;
                if (options.params.cm_re)
                    nfTrailing += `&cm_re=${encodeParam(options.params.cm_re)}`;
                selectedFacets.push({
                    facetName: 'Price Range',
                    facetId: hashCode('S1_Price_Range'),
                    facetType: 'prange',
                    displayFacetName: true,
                    facetInternalName: 'S1_Price_Range',
                    facetValueList: [
                        {
                            name: `$${nfM[1]} - $${nfM[2]}`,
                            selected: true,
                            count: 0,
                            apiUrl: buildFacetUrl(pathOnly, nfFacetsMap, anchorParam, nfTrailing),
                            facetNValue: '',
                            facetValue: `$${nfM[1]} - $${nfM[2]}`,
                            solrFacetName: 'Nf',
                        },
                    ],
                });
            }
        }

        // Inject custom discount range (Dr) as selected facet chip
        if (
            options &&
            options.params &&
            options.params.Dr &&
            typeof options.params.Dr === 'string'
        ) {
            const drVal = options.params.Dr.replace(/\+/g, ' ').trim();
            const drM = drVal.match(/^DR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
            if (drM) {
                const drFacetsMap = {};
                facets.forEach((f) => {
                    if (
                        !f ||
                        !Array.isArray(f.options) ||
                        f.name === 'store_availability' ||
                        f.name === 'store_ids'
                    )
                        return;
                    const selected = f.options
                        .filter((o) => o && o.status === 'selected' && o.value)
                        .map((o) => o.value);
                    if (selected.length > 0)
                        drFacetsMap[f.name] = encodeParam(selected.sort().join('|'));
                });
                let drTrailing =
                    (currentSort ? `&sort=${currentSort}` : '') +
                    (options.params.Nf ? `&Nf=${encodeParam(options.params.Nf)}` : '');
                if (options.params.storeIds)
                    drTrailing += `&storeIds=${options.params.storeIds}&store_availability=same+day+pickup`;
                if (options.params.cm_re)
                    drTrailing += `&cm_re=${encodeParam(options.params.cm_re)}`;
                selectedFacets.push({
                    facetName: 'Discount Range',
                    facetId: hashCode('S1_Discount_Range'),
                    facetType: 'prange',
                    displayFacetName: true,
                    facetInternalName: 'S1_Discount_Range',
                    facetValueList: [
                        {
                            name: `${drM[1]}% - ${drM[2]}%`,
                            selected: true,
                            count: 0,
                            apiUrl: buildFacetUrl(pathOnly, drFacetsMap, anchorParam, drTrailing),
                            facetNValue: '',
                            facetValue: `${drM[1]}% - ${drM[2]}%`,
                            solrFacetName: 'Dr',
                        },
                    ],
                });
            }
        }

        // Transform related_searches from Constructor response to Solr relatedTerms format
        // Input: [{query, image_url}, ...] (e.g., {query: "blue pants", image_url: "DP0707202509211301M"})
        // Output: [{text, apiUrl, productImgId}, ...] (Solr format for display)
        // Controlled via preferences.constructorIO.common.enableRelatedSearchTransform (default: true)
        const enableRelatedSearchTransform =
            options && options.enableRelatedSearchTransform !== false;
        let relatedTerms = [];
        if (enableRelatedSearchTransform) {
            const rawRelatedSearches =
                response && Array.isArray(response.related_searches)
                    ? response.related_searches
                    : [];
            relatedTerms = rawRelatedSearches
                .map((item) => {
                    // Null-safety: skip null/undefined items
                    if (!item || typeof item !== 'object') return null;
                    const text = (item.query && String(item.query)) || '';
                    if (!text) return null;
                    // Build apiUrl in Solr format: /v1/search-service/s?searchTerm=<text>
                    const encodedTerm = encodeURIComponent(text).replace(/%20/g, '+');
                    const apiUrl = `/v1/search-service/s?searchTerm=${encodedTerm}`;
                    const productImgId = (item.image_url && String(item.image_url)) || '';
                    // Determine sourceType based on image ID pattern:
                    // - If image starts with "DP" (uppercase) → standard product image, no sourceType needed
                    // - If image doesn't start with "DP" → marketing/external image, sourceType = 'MKL'
                    let sourceType = null;
                    if (productImgId && !productImgId.startsWith('DP')) {
                        sourceType = 'MKL';
                    }
                    return { text, apiUrl, productImgId, sourceType };
                })
                .filter(Boolean);
        }

        let requestUrl = `/api${baseApiPath}`;
        if (options && options.params) {
            const p = options.params;
            if (p.storeIds) requestUrl += `&storeIds=${encodeURIComponent(p.storeIds)}`;
            if (p.store_availability)
                requestUrl += `&store_availability=${encodeURIComponent(p.store_availability)}`;
            if (p.geoZip) requestUrl += `&geoZip=${encodeURIComponent(p.geoZip)}`;
        }

        return {
            requestDateTime: new Date().toString(),
            channelName: 'CONSTRUCTOR-IO',
            resultId,
            requestUrl,
            contentType: 'XGNS',
            statusCode: response.redirect ? 302 : 200,
            type: 'XGNS',
            numOfMarketingSlots: 0,
            enableRelatedSearch: true,
            retainMarketingZone: false,
            redirectProductPage: response.redirect !== null,
            keywordRedirectUrl: (() => {
                const redirectUrl =
                    (response.redirect && response.redirect.data && response.redirect.data.url) ||
                    '';
                if (!redirectUrl) return '';
                // If Constructor provides a message with the redirect, append addmsg=true
                // so the target page knows to display the redirect message
                const message =
                    response.redirect && response.redirect.data && response.redirect.data.message;
                if (message) {
                    const sep = redirectUrl.includes('?') ? '&' : '?';
                    return `${redirectUrl}${sep}addmsg=true`;
                }
                return redirectUrl;
            })(),
            keywordRedirectMessage: (() => {
                const msg =
                    (response.redirect &&
                        response.redirect.data &&
                        response.redirect.data.message) ||
                    '';
                return msg.replace(/<search term>/gi, term);
            })(),
            pdPageURL: (() => {
                // Gallery/browse pages (/g) — never redirect to PDP on single result
                // User is browsing a category, single result should stay in grid
                const isBrowsePage = options && options.pageType === 'g';
                if (isBrowsePage) return '';

                if (!response.redirect && total === 1 && results.length === 1) {
                    const singleUrl = (results[0] && results[0].data && results[0].data.url) || '';
                    if (singleUrl) {
                        let redirectUrl = singleUrl;
                        // Append storeIds to single-result redirect URL (same-day-pickup)
                        if (options && options.params && options.params.storeIds) {
                            const sep = redirectUrl.includes('?') ? '&' : '?';
                            redirectUrl += `${sep}storeIds=${encodeURIComponent(
                                options.params.storeIds
                            )}`;
                        }
                        // Append geoZip to single-result redirect URL (localization)
                        if (options && options.params && options.params.geoZip) {
                            const sep = redirectUrl.includes('?') ? '&' : '?';
                            redirectUrl += `${sep}geoZip=${encodeURIComponent(
                                options.params.geoZip
                            )}`;
                        }
                        return redirectUrl;
                    }
                }
                return '';
            })(),
            pageCanonicalURL: buildCanonicalURL(
                term,
                facets,
                pageType,
                categoryId,
                galleryPath,
                brandParam
            ),
            displayFreeSameDayPickup: (() => {
                const storeAvailFacet = facets.find((f) => f && f.name === 'store_availability');
                if (!storeAvailFacet || !Array.isArray(storeAvailFacet.options)) return false;
                return storeAvailFacet.options.some(
                    (opt) =>
                        opt &&
                        (opt.value === 'both' ||
                            opt.value === 'same day pickup' ||
                            opt.display_name === 'same day pickup')
                );
            })(),
            visNavExist: false,
            showAutoscopeMsg: false,
            organicZoneInfo: {
                totalNumRecs: total,
                totalPages,
                currentPage: page,
                nextPage: page < totalPages,
                prevPage: page > 1,
                nextPageNum: page < totalPages ? page + 1 : 0,
                prevPageNum: page > 1 ? page - 1 : 0,
                nextPageOffset: page < totalPages ? page * perPage : 0,
                prevPageOffset: page > 1 ? (page - 2) * perPage : 0,
                ppConsumed: 0,
                nextPageMktTiles: 0,
                galleryMLRanking: false,
                inStockZero: false,
                multipleThumbNail: false,
                brandPage: false,
                products: results.map((r) =>
                    transformProduct(r, term, {
                        templateTypesForMiniPdp: options && options.templateTypesForMiniPdp,
                        promotionExcludeLabels: options && options.promotionExcludeLabels,
                        params: options && options.params,
                        pageType,
                        categoryId: options && options.categoryId,
                        groupImageTypes: options && options.groupImageTypes,
                        isColorFilterSelected: options && options.isColorFilterSelected,
                        previewDayKey: options && options.previewDayKey,
                        previewMinutes: options && options.previewMinutes,
                        selectedFacets: (() => {
                            // Detect which PDP-enabled facets are active in URL params
                            // Default list matches Solr's pdpEnabledFacetNames config
                            const pdpEnabledFacets = (options && options.pdpEnabledFacets) || [
                                'color',
                                'size',
                                'childrens_sizes',
                                'bra_size',
                                'mens_sizes',
                                'bed_size',
                                'size_range',
                                'mens_size_range',
                                'womens_size_range',
                            ];
                            // Map Constructor facet names to facetSelected group names (matches Solr pdpEnabledFacetNames)
                            // Configurable via preferences.constructorIO.search.pdpFacetGroupMap
                            const defaultFacetGroupMap = {
                                color: 'color',
                                size: 'size',
                                childrens_sizes: 'size',
                                bra_size: 'size',
                                mens_sizes: 'size',
                                bed_size: 'product',
                                size_range: 'sizerange',
                                mens_size_range: 'sizerange',
                                womens_size_range: 'sizerange',
                            };
                            const facetGroupMap =
                                (options && options.pdpFacetGroupMap) || defaultFacetGroupMap;
                            const active = [];
                            if (options && options.params) {
                                pdpEnabledFacets.forEach((facet) => {
                                    const key = Object.keys(options.params).find(
                                        (k) => k.toLowerCase() === facet.toLowerCase()
                                    );
                                    if (key && options.params[key]) {
                                        const groupName = facetGroupMap[facet] || facet;
                                        if (!active.includes(groupName)) {
                                            active.push(groupName);
                                        }
                                    }
                                });
                            }
                            return active;
                        })(),
                    })
                ),
            },
            facets: {
                orderedSelectedFacetList: selectedFacets,
                facetList: facets
                    // Filter out facets based on page type
                    // hideFacetsOnSearch: array of facet names to hide on search pages (/s)
                    // hideFacetsOnBrowse: array of facet names to hide on browse pages (/g)
                    .filter((f) => {
                        if (!f || f.name === 'store_ids') return false;
                        const hideFacetsOnSearch = (options && options.hideFacetsOnSearch) || [];
                        const hideFacetsOnBrowse = (options && options.hideFacetsOnBrowse) || [];
                        if (pageType === 's' && hideFacetsOnSearch.includes(f.name)) return false;
                        if (pageType === 'g' && hideFacetsOnBrowse.includes(f.name)) return false;
                        if (f.hidden) return false;
                        return true;
                    })
                    .map((f) => {
                        const previewDateFacetOptions =
                            (options && options.previewDateFacetOptions) || [];
                        const facetPreviewDayKey = options && options.previewDayKey;
                        if (
                            isPreviewHiddenFacet(
                                f.name,
                                facetPreviewDayKey,
                                previewDateFacetOptions
                            ) &&
                            Array.isArray(f.options)
                        ) {
                            // eslint-disable-next-line no-param-reassign
                            f = {
                                ...f,
                                options: f.options.filter((opt) => {
                                    if (!opt || typeof opt.value !== 'string') return false;
                                    const win = parsePreviewWindow(opt.value);
                                    return win && win.date === facetPreviewDayKey;
                                }),
                            };
                        }
                        // Map store_availability "both" value to "same day pickup" for URLs
                        if (f.name === 'store_availability' && Array.isArray(f.options)) {
                            const mappedFacet = {
                                ...f,
                                options: f.options.map((opt) => {
                                    if (
                                        opt &&
                                        (opt.value === 'both' ||
                                            opt.value === 'same day pickup' ||
                                            opt.display_name === 'same day pickup')
                                    ) {
                                        return {
                                            ...opt,
                                            value: 'same day pickup',
                                            display_name: 'same day pickup',
                                            ...(options?.params?.storeIds
                                                ? { status: 'selected' }
                                                : {}),
                                        };
                                    }
                                    return opt;
                                }),
                            };
                            return transformFacet(
                                mappedFacet,
                                term,
                                currentSort,
                                facets,
                                options && options.params,
                                pageType,
                                categoryId,
                                galleryPath,
                                brandParam,
                                previewDateFacetOptions,
                                facetPreviewDayKey,
                                options && options.appliedFilters,
                                true,
                                options && options.previewMinutes
                            );
                        }
                        return transformFacet(
                            f,
                            term,
                            currentSort,
                            facets,
                            options && options.params,
                            pageType,
                            categoryId,
                            galleryPath,
                            brandParam,
                            previewDateFacetOptions,
                            facetPreviewDayKey,
                            options && options.appliedFilters,
                            true,
                            options && options.previewMinutes
                        );
                    })
                    .filter(Boolean)
                    .filter((facet) => {
                        // Skip facets with only 1 option value (matches Solr behavior)
                        // unless a facet value is already selected, or it's store_availability (always needed for toggle)
                        if (facet.facetInternalName === 'store_availability') return true;
                        if (facet.activeFacet) return true;
                        return facet.facetValueCount > 1;
                    })
                    .reduce((acc, facet) => {
                        // Max facet categories limit (default 12, configurable)
                        // store_availability doesn't count toward the limit (it's a special toggle)
                        const maxFacets = (options && options.maxFacetCategories) || 12;
                        if (facet.facetInternalName === 'store_availability') {
                            acc.push(facet);
                        } else if (
                            acc.filter((f) => f.facetInternalName !== 'store_availability').length <
                            maxFacets
                        ) {
                            acc.push(facet);
                        }
                        return acc;
                    }, [])
                    .map((facet) => {
                        // Retain store params in apiUrls (skip store_availability — used for toggle OFF)
                        if (
                            !options?.params?.storeIds ||
                            facet.facetInternalName === 'store_availability'
                        )
                            return facet;
                        const storeSuffix = `&storeIds=${options.params.storeIds}`;
                        if (facet.facetValueList) {
                            // eslint-disable-next-line no-param-reassign
                            facet.facetValueList = facet.facetValueList.map((fv) => {
                                if (!fv.apiUrl) return fv;
                                /* store_availability is in facetsMap (sorts before id=) — do not strip it.
                                 * Only ensure storeIds is present as a trailing param. */
                                let url = fv.apiUrl.replace(/&store_ids=[^&]*/g, '');
                                if (!url.includes('storeIds')) url += storeSuffix;
                                return { ...fv, apiUrl: url };
                            });
                        }
                        return facet;
                    }),
                regionalfacetMap: {},
                contentType: 'GuidedNavigation',
                showClearAll: (() => {
                    let totalSelected = 0;
                    facets.forEach((f) => {
                        if (f && Array.isArray(f.options) && !isStoreFacet(f.name)) {
                            f.options.forEach((o) => {
                                if (o && o.status === 'selected') totalSelected++;
                            });
                        }
                    });
                    // Count Nf/Dr as selected facets for Clear All visibility
                    if (options && options.params && options.params.Nf) totalSelected++;
                    if (options && options.params && options.params.Dr) totalSelected++;
                    return totalSelected >= 2;
                })(),
                clearAll: `${baseApiPath}&productGridView=medium${
                    currentSort ? `&sort=${currentSort}` : ''
                }`,
                noOfSelectedFacets: selectedFacets.length,
                galleryFacetReorderingIndicator: false,
            },
            sortOptions: transformSortOptions(
                sortOptions,
                term,
                facets,
                options && options.params,
                pageType,
                categoryId,
                galleryPath,
                brandParam,
                options && options.previewDayKey,
                options && options.previewDateFacetOptions
            ),
            relatedTerms,
        };
    } catch (error) {
        return getEmptyResponse(pageType, categoryId, galleryPath, brandParam);
    }
};

function getEmptyResponse(pageType, categoryId, galleryPath, brandParam) {
    let basePath;
    let canonicalPath;
    if (pageType === 'g') {
        if (categoryId) {
            basePath = `/v1/search-service/${galleryPath || 'g'}?id=${encodeURIComponent(
                categoryId
            )}`;
            canonicalPath = `/${galleryPath || 'g'}?id=${encodeURIComponent(categoryId)}`;
        } else if (brandParam) {
            basePath = `/v1/search-service/${galleryPath || 'g'}?brand=${encodeURIComponent(
                brandParam
            )}`;
            canonicalPath = `/${galleryPath || 'g'}?brand=${encodeURIComponent(brandParam)}`;
        } else {
            basePath = '/v1/search-service/s?searchTerm=';
            canonicalPath = '/s?searchTerm=';
        }
    } else {
        basePath = '/v1/search-service/s?searchTerm=';
        canonicalPath = '/s?searchTerm=';
    }
    return {
        requestDateTime: new Date().toString(),
        channelName: 'CONSTRUCTOR-IO',
        resultId: '',
        requestUrl: `/api${basePath}`,
        contentType: 'XGNS',
        statusCode: 200,
        type: 'XGNS',
        numOfMarketingSlots: 0,
        enableRelatedSearch: false,
        retainMarketingZone: false,
        redirectProductPage: false,
        pageCanonicalURL: canonicalPath,
        displayFreeSameDayPickup: true,
        visNavExist: false,
        showAutoscopeMsg: false,
        organicZoneInfo: {
            totalNumRecs: 0,
            totalPages: 0,
            currentPage: 1,
            nextPage: false,
            prevPage: false,
            nextPageNum: 0,
            prevPageNum: 0,
            nextPageOffset: 0,
            prevPageOffset: 0,
            ppConsumed: 0,
            nextPageMktTiles: 0,
            galleryMLRanking: false,
            inStockZero: false,
            multipleThumbNail: false,
            brandPage: false,
            products: [],
        },
        facets: {
            orderedSelectedFacetList: [],
            facetList: [],
            regionalfacetMap: {},
            contentType: 'GuidedNavigation',
            showClearAll: false,
            clearAll: `${basePath}&productGridView=medium`,
            noOfSelectedFacets: 0,
            galleryFacetReorderingIndicator: false,
        },
        sortOptions: [
            {
                apiUrl: '',
                name: pageType === 'g' ? 'featured' : 'best match',
                key: '',
                order: 1,
                selected: true,
            },
            { apiUrl: '', name: 'new arrivals', key: 'NA', order: 2, selected: false },
            { apiUrl: '', name: 'best sellers', key: 'BS', order: 3, selected: false },
            { apiUrl: '', name: 'ratings high - low', key: 'RHL', order: 4, selected: false },
            { apiUrl: '', name: 'price low - high', key: 'LPLH', order: 5, selected: false },
            { apiUrl: '', name: 'price high - low', key: 'LPHL', order: 6, selected: false },
        ],
        relatedTerms: [],
    };
}

/**
 * Parses a DP_PREVIEW_DATE cookie string (e.g. "2024.01.15 2:30 PM") into ISO format
 * (e.g. "2024-01-15T14:30:00.000Z"). Valid dates are today through 21 days out (22 days total).
 * Returns current date if the value is missing, unparseable, or outside valid dates.
 */
const PREVIEW_LEAD_DAYS = 21;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/* Preview datetimes (DP_PREVIEW_DATE cookie) and the time-level markers / pf keys are all authored in US Central wall-clock. */
const PREVIEW_TIME_ZONE = 'America/Chicago';

/**
 * Returns the Central-time calendar fields for a given instant, DST-aware.
 * @param {Date} date
 * @returns {{ year: number, month: number, day: number, hours: number, minutes: number }}
 *          month is 1-based; hours is 0-23.
 */
function getCentralFields(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: PREVIEW_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return {
        year: parseInt(get('year'), 10),
        month: parseInt(get('month'), 10),
        day: parseInt(get('day'), 10),
        hours: parseInt(get('hour'), 10),
        minutes: parseInt(get('minute'), 10),
    };
}

/* Formats Central-time calendar fields as YYYY.MM.DD for lexicographic comparison. */
function centralDayStr(fields) {
    return `${fields.year}.${String(fields.month).padStart(2, '0')}.${String(fields.day).padStart(
        2,
        '0'
    )}`;
}

/**
 * Converts Central wall-clock calendar fields to a UTC ISO string, using the actual Central offset in effect on that date (DST-aware).
 *
 * @param {number} y  full year
 * @param {number} m  1-based month
 * @param {number} d  day
 * @param {number} h  hours 0-23
 * @param {number} min minutes
 * @returns {string} UTC ISO string
 */
function centralFieldsToIso(y, m, d, h, min) {
    const guessUtcMs = Date.UTC(y, m - 1, d, h, min);
    const central = getCentralFields(new Date(guessUtcMs));
    const centralAsUtcMs = Date.UTC(
        central.year,
        central.month - 1,
        central.day,
        central.hours,
        central.minutes
    );
    // offset = how much Central is behind UTC at this instant (e.g. +6h for CST, +5h for CDT).
    const offsetMs = guessUtcMs - centralAsUtcMs;
    return new Date(guessUtcMs + offsetMs).toISOString();
}

/**
 * Shared parser + validator for the DP_PREVIEW_DATE cookie.
 *
 * @param {string} dateStr - e.g. "2026.09.29 10:04 am"
 * @returns {{ valid: boolean, year: number, month: number, day: number, hours: number, minutes: number }}
 *          When invalid/out-of-range, valid=false and the fields carry Central "now".
 */
function resolveCentralPreviewFields(dateStr) {
    const nowCentral = getCentralFields(new Date());
    const invalid = { valid: false, ...nowCentral };

    if (!dateStr || typeof dateStr !== 'string') return invalid;

    const match = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})\s+(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!match) return invalid;
    const [, year, month, day, rawHours, minutes, meridiem] = match;

    // Range check in Central: today .. today + PREVIEW_LEAD_DAYS.
    const inputDate = `${year}.${month}.${day}`;
    const todaysDate = centralDayStr(nowCentral);
    const maxDateStr = centralDayStr(
        getCentralFields(new Date(Date.now() + PREVIEW_LEAD_DAYS * MS_PER_DAY))
    );
    if (inputDate < todaysDate || inputDate > maxDateStr) return invalid;

    let hours = parseInt(rawHours, 10);
    const mer = meridiem.toLowerCase();
    if (mer === 'pm' && hours !== 12) hours += 12;
    if (mer === 'am' && hours === 12) hours = 0;

    return {
        valid: true,
        year: +year,
        month: +month,
        day: +day,
        hours,
        minutes: parseInt(minutes, 10),
    };
}

/**
 * Parses a DP_PREVIEW_DATE cookie string (e.g. "2024.01.15 2:30 PM") into a UTC ISO string (e.g. "2024-01-15T20:30:00.000Z") for the Constructor "now" param.
 * Valid dates are Central today through 21 days out (22 days total). Returns the current time if the value is missing, unparseable, or outside valid dates.
 */
export function parsePreviewDateToIso(dateStr) {
    const f = resolveCentralPreviewFields(dateStr);
    if (!f.valid) return new Date().toISOString();
    return centralFieldsToIso(f.year, f.month, f.day, f.hours, f.minutes);
}

/**
 * Extracts the Central-time preview key parts from the DP_PREVIEW_DATE cookie string.
 *
 * The cookie is authored in Central wall-clock (e.g. "2026.09.29 10:04 am"), and the
 * time-level markers / pf keys are also Central wall-clock. So the day key (DDMonYYYY)
 * and the minutes-of-day used for window selection are taken from the Central fields
 *
 * @param {string} dateStr - the DP_PREVIEW_DATE cookie value, e.g. "2026.09.29 10:04 am"
 * @returns {{ dayKey: string, minutes: number }}
 *          dayKey is DDMonYYYY (e.g. "29Sep2026"); minutes is minutes-of-day 0-1439, Central.
 */
export function getPreviewCentralKey(dateStr) {
    const f = resolveCentralPreviewFields(dateStr);
    return {
        dayKey: `${String(f.day).padStart(2, '0')}${MONTH_ABBR[f.month - 1]}${f.year}`,
        minutes: f.hours * 60 + f.minutes,
    };
}

/**
 * Default list of system/non-facet URL params that should NOT be sent as
 * filters to Constructor.io search API.
 *
 * Can be extended/reduced at runtime via preferences config:
 *   preferences.constructorIO.common.ignoreFilterParams = { add: [], remove: [] }
 */
export { CONSTRUCTOR_IGNORE_FILTER_PARAMS };

/**
 * Returns the final Set of param names to ignore when building Constructor filters.
 * Merges the default list with optional preferences overrides:
 *   preferences.constructorIO.common.ignoreFilterParams.add    → extra params to exclude
 *   preferences.constructorIO.common.ignoreFilterParams.remove → params to stop excluding
 *
 * All param names are normalized to lowercase for case-insensitive matching.
 *
 * @param {Object} preferences - The preferences object from context
 * @returns {Set<string>} - Set of param names to ignore (all lowercase)
 */
export function getConstructorIgnoreParams(preferences) {
    // Normalize all default params to lowercase for case-insensitive matching
    const ignoreSet = new Set(
        CONSTRUCTOR_IGNORE_FILTER_PARAMS.map((p) => (p ? p.toLowerCase() : p))
    );

    const config =
        preferences &&
        preferences.constructorIO &&
        preferences.constructorIO.common &&
        preferences.constructorIO.common.ignoreFilterParams;

    if (config) {
        if (Array.isArray(config.add)) {
            config.add.forEach((param) => {
                if (param) ignoreSet.add(param.toLowerCase());
            });
        }
        if (Array.isArray(config.remove)) {
            config.remove.forEach((param) => {
                if (param) ignoreSet.delete(param.toLowerCase());
            });
        }
    }

    return ignoreSet;
}

export default transformConstructorSearchResponse;

/**
 * Translates Solr Nf/Dr URL params to Constructor filter format.
 * Used by GallerySaga and BOPISSaga to apply price/discount range filters.
 *
 * @param {object} params - URL params object (must have Nf and/or Dr keys)
 * @param {object} searchConfig - constructorIO.search preferences
 * @param {object} filters - filters object to mutate (adds filters[field]=value)
 */
export function applyRangeFilters(params, searchConfig, filters) {
    if (!params || !filters) return;
    const config = searchConfig || {};

    // Nf (Price Range): "PR 10 50" → filters[effective_price_min]=10-50
    if (params.Nf && typeof params.Nf === 'string') {
        const nfValue = params.Nf.replace(/\+/g, ' ').trim();
        const nfMatch = nfValue.match(/^PR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
        if (nfMatch) {
            const priceField = config.priceRangeField || 'effective_price_min';
            filters[`filters[${priceField}]`] = [`${nfMatch[1]}-${nfMatch[2]}`];
        }
    }

    // Dr (Discount Range): "DR 10 50" → filters[min_prc_dscntd_pc]=10-50
    if (params.Dr && typeof params.Dr === 'string') {
        const drValue = params.Dr.replace(/\+/g, ' ').trim();
        const drMatch = drValue.match(/^DR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
        if (drMatch) {
            const discountField = config.discountRangeField || 'min_prc_dscntd_pc';
            filters[`filters[${discountField}]`] = [`${drMatch[1]}-${drMatch[2]}`];
        }
    }
}
